import type React from 'react';
import { memo, useMemo } from 'react';
import type { Modifier } from 'react-day-picker';
import type { ValueType } from 'react-select/lib/types';

import * as styles from './styles';
import { debounce } from 'lodash';
import moment from 'moment-timezone';

import { MediaType, type Trip } from '@cobliteam/common-types';

import { useCameraTrip } from '@cobliteam/hooks';
import { useGetMediaRequestList } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import {
  CalendarStepIcon,
  CaptionDefault,
  CaptionMedium,
  DescriptionTitle,
  Divider,
  HintPopup,
  HourInput,
  type ISelectOptionType,
  Label,
  Select,
  SingleDatePicker,
} from '@cobliteam/base-components';

import DateRangeIcon from '@mui/icons-material/DateRange';

import useMediaStore from '../../../../store/zustand/useMediaStore';
import {
  DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY,
  DEFAULT_MEDIA_REQUEST_STATUS,
  getPeriodErrorMessage,
  MEDIA_REQUEST_MODAL_BASE_PERIOD,
  MEDIA_REQUEST_MODAL_MEDIAS_LIMIT,
  MEDIA_REQUEST_MODAL_TRIPS_LIMIT,
} from '../../../../utils/mediaUtils';

interface PeriodOptionItem {
  label: string;
  value: string;
  isDisabled: boolean;
  trip: Trip;
  overnightTripStartTimestamp?: number;
}

const getPeriodOptions = (timestampInMillis: number, vehiTrips: Trip[]): PeriodOptionItem[] => {
  const momentDay = moment(timestampInMillis);
  const tripsFromDay = vehiTrips.filter((value) => value.startTimestamp >= momentDay.startOf('day').valueOf() && value.startTimestamp <= momentDay.endOf('day').valueOf());
  const options = tripsFromDay.map((value, index) => {
    const label = `Período ${index + 1} | ${moment(value.startTimestamp).format('HH:mm')} - ${moment(value.endTimestamp).format('HH:mm')}`;

    return {
      label,
      value: `${value.startTimestamp}`,
      isDisabled: !value.isFootageAvailable,
      trip: value,
      overnightTripStartTimestamp: value.overnightTripStartTimestamp,
    };
  });

  return options;
};
const getDates = (startDate: Date, endDate: Date) => {
  const fromDate = moment(startDate).startOf('day');
  const toDate = moment(endDate).endOf('day');
  const span = Math.floor(moment.duration(toDate.diff(fromDate)).asDays());
  const days = [];

  for (let i = 0; i <= span; i++) {
    days.push(moment(fromDate).add(i, 'day').startOf('day').toDate());
  }

  return days;
};
const lastAvailableDays = getDates(MEDIA_REQUEST_MODAL_BASE_PERIOD.before, MEDIA_REQUEST_MODAL_BASE_PERIOD.after);

export const PeriodSelectionStep: React.FC = memo(() => {
  const mediaRequestData = useMediaStore((state) => state.requestMediaData);
  const setMediaRequestData = useMediaStore((state) => state.setRequestMediaData);

  const { getCameraTrip } = useCameraTrip();
  const { data: tripsPagination, isFetching: isFetchingTrips } = getCameraTrip(
    mediaRequestData!.vehicleId!,
    MEDIA_REQUEST_MODAL_BASE_PERIOD.before.valueOf(),
    MEDIA_REQUEST_MODAL_BASE_PERIOD.after.valueOf(),
    DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.min,
    MEDIA_REQUEST_MODAL_TRIPS_LIMIT.max,
    { column: 'startTime', order: 'desc' },
    { enabled: !!mediaRequestData?.vehicleId }
  );

  const { data: mediasPage, isLoading: isLoadingMediasPage } = useGetMediaRequestList({
    page: MEDIA_REQUEST_MODAL_MEDIAS_LIMIT.min,
    size: MEDIA_REQUEST_MODAL_MEDIAS_LIMIT.max,
    mediaTypes: [MediaType.FOOTAGE_VIDEO, MediaType.TIMELAPSE],
    vehicleIds: [mediaRequestData!.vehicleId!],
    requestStatus: DEFAULT_MEDIA_REQUEST_STATUS,
    mediaPeriod: {
      start: MEDIA_REQUEST_MODAL_BASE_PERIOD.before.valueOf(),
      end: MEDIA_REQUEST_MODAL_BASE_PERIOD.after.valueOf(),
    },
  });

  const [modifiers, tripsData] = useMemo(() => {
    if (tripsPagination?.content?.length) {
      const normalizedTripsData: Trip[] = [];
      const availableDays: Modifier[] = [];
      const expiredDays: Modifier[] = [];
      const noActivityDays: Modifier[] = [];

      tripsPagination.content.forEach((trip) => {
        const tripStart = moment(trip.startTimestamp).startOf('day');
        const tripEnd = moment(trip.endTimestamp).startOf('day');

        if (tripEnd.isAfter(tripStart)) {
          const sameDayTripSequence: Trip = {
            ...trip,
            endTimestamp: tripStart.endOf('day').valueOf(),
          };
          const overnightTripSequence: Trip = {
            ...trip,
            overnightTripStartTimestamp: trip.startTimestamp,
            startTimestamp: tripEnd.valueOf(),
          };

          normalizedTripsData.push(sameDayTripSequence, overnightTripSequence);
        } else {
          normalizedTripsData.push(trip);
        }
      });

      lastAvailableDays.forEach((date) => {
        const tripsOnDay = normalizedTripsData.filter((trip) => moment(trip.startTimestamp).startOf('day').valueOf() === date.valueOf());

        if (tripsOnDay.length === 0) {
          noActivityDays.push(date);
        } else {
          const availables = tripsOnDay.filter((trip) => trip.isFootageAvailable);

          if (availables.length === 0) {
            expiredDays.push(date);
          } else {
            availableDays.push(date);
          }
        }
      });

      const sliceIndex = Math.ceil(availableDays.length / 5);
      const toBeExpiredDays: Modifier[] = sliceIndex ? availableDays.slice().splice(0, sliceIndex) : [];
      const recentDays: Modifier[] = sliceIndex ? availableDays.slice().splice(sliceIndex, availableDays.length - 1) : [];
      const trips = normalizedTripsData.reverse();
      const mods = {
        recent: recentDays,
        noActivity: noActivityDays,
        toBeExpired: toBeExpiredDays,
        expired: expiredDays,
        outOfRange: MEDIA_REQUEST_MODAL_BASE_PERIOD,
      };

      const startOfDay = mediaRequestData?.requestPeriodStart
        ? moment(mediaRequestData?.requestPeriodStart)
        : moment(normalizedTripsData[normalizedTripsData.length - 1].startTimestamp);

      setMediaRequestData({
        ...mediaRequestData,
        requestDate: mediaRequestData?.requestDate || startOfDay.startOf('day').valueOf(),
      });

      return [mods, trips];
    }

    return [{}, []];
  }, [JSON.stringify(tripsPagination)]);

  const onDayChange = (timestampInMillis: number) => {
    setMediaRequestData({
      ...mediaRequestData,
      requestDate: timestampInMillis,
      requestPeriodStart: undefined,
      requestMinute: undefined,
    });
  };

  const onPeriodChange = (period: ValueType<ISelectOptionType>) => {
    const { value, overnightTripStartTimestamp, trip } = period as PeriodOptionItem;
    const startTime = (value && Number.parseInt(value, 10)) as number;
    const startTimeMoment = moment(startTime);
    const hour = startTimeMoment.hour();
    const minute = startTimeMoment.minute();

    setMediaRequestData({
      ...mediaRequestData,
      requestPeriodStart: overnightTripStartTimestamp || startTime,
      requestMinute: mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO ? `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}` : undefined,
      tripId: trip.id,
    });
  };

  const onMinuteChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setMediaRequestData({
      ...mediaRequestData,
      requestMinute: event.target.value,
    });
  };

  const getDisabledOptions = (option: ISelectOptionType) => !!option.isDisabled;

  const formatOptionLabel = (option: ISelectOptionType) => {
    const { label, isDisabled } = option;

    const disabledOption = (
      <HintPopup title='Gravação expirada' placement='right' arrow>
        <div className={styles.disabledOption}>
          {label}
          <div />
        </div>
      </HintPopup>
    );

    return isDisabled ? disabledOption : label;
  };

  const periodOptions = useMemo(
    () => (mediaRequestData?.requestDate && tripsData.length ? getPeriodOptions(mediaRequestData?.requestDate, tripsData) : []),
    [mediaRequestData?.requestDate, JSON.stringify(tripsData)]
  );

  const debouncedOnMinuteChange = useMemo(() => debounce(onMinuteChange, 300), [JSON.stringify(mediaRequestData)]);

  const selectedPeriod = useMemo(
    () => (mediaRequestData?.requestPeriodStart && periodOptions ? periodOptions.find((e) => e.value === mediaRequestData.requestPeriodStart!.toString()) : null),
    [mediaRequestData?.requestPeriodStart, JSON.stringify(periodOptions)]
  );

  const errorMessage = useMemo(
    () => getPeriodErrorMessage(mediaRequestData, mediasPage?.content, selectedPeriod?.trip),
    [JSON.stringify(mediaRequestData), JSON.stringify(mediasPage?.content), selectedPeriod?.trip]
  );

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.leftSideWrapper}>
        <div className={styles.headerMargin}>
          <div className={styles.headerIconLine}>
            <DateRangeIcon />
            <DescriptionTitle>Selecionar data</DescriptionTitle>
          </div>
          <CaptionDefault>
            A câmera armazena 100 horas de gravação corrida, após esse limite, o dispositivo passa a substituir os vídeos mais antigos pelos mais recentes.
          </CaptionDefault>
        </div>
        <Divider />
        <div>
          <div className={styles.dateSelectionSection}>
            <div>
              <Label>Selecione a Data</Label>
            </div>
            <div className={styles.datePicker}>
              <SingleDatePicker
                handleDayChange={onDayChange}
                selectedDateInMillis={mediaRequestData?.requestDate}
                disabled={isFetchingTrips || tripsPagination?.content.length === 0}
                modifiers={modifiers}
                fromMonth={MEDIA_REQUEST_MODAL_BASE_PERIOD.before}
                toMonth={MEDIA_REQUEST_MODAL_BASE_PERIOD.after}
                calendarFootnotes={
                  <div className={styles.calendarFootnotes}>
                    <Divider />
                    <div className={styles.calendarFootnotesColors}>
                      <div>
                        <CaptionMedium>Legenda:</CaptionMedium>
                      </div>
                      <div>
                        <div />
                        <CaptionDefault>Sem atividade</CaptionDefault>
                      </div>
                      <div>
                        <div />
                        <CaptionDefault>Recente</CaptionDefault>
                      </div>
                      <div>
                        <div />
                        <CaptionDefault>Próximo a vencer</CaptionDefault>
                      </div>
                      <div>
                        <div />
                        <CaptionDefault>Gravação Expirada</CaptionDefault>
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
          <div className={styles.periodAndHourDiv}>
            <div className={styles.periodSelectDiv}>
              <Label>Selecione um período</Label>
              <Select
                value={selectedPeriod}
                options={periodOptions}
                onChange={onPeriodChange}
                placeholder='Selecione um período'
                isLoading={isFetchingTrips || isLoadingMediasPage}
                isDisabled={isFetchingTrips || isLoadingMediasPage}
                errorMessage={mediaRequestData!.mediaType === MediaType.TIMELAPSE ? errorMessage : undefined}
                isOptionDisabled={getDisabledOptions}
                formatOptionLabel={formatOptionLabel}
              />
            </div>
            {mediaRequestData!.mediaType === MediaType.FOOTAGE_VIDEO && (
              <div>
                <Label>Minuto do período</Label>
                <div className={styles.timePickerWrapper}>
                  <HourInput
                    className={styles.timePicker}
                    onChange={debouncedOnMinuteChange}
                    value={mediaRequestData?.requestMinute || null}
                    error={!!errorMessage}
                    disabled={!mediaRequestData?.requestPeriodStart}
                  />
                  {!!errorMessage && <CaptionDefault className={styles.inputErrorLabel}>{errorMessage}</CaptionDefault>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.rightSideWrapper}>
        <CalendarStepIcon />
      </div>
    </div>
  );
});

export default PeriodSelectionStep;
